/**
 * 송금 관리 데이터 훅
 */
import {useCallback, useState} from 'react';
import {transferApi} from '../api/transferApi';
import {
    PostTransferRequestDto,
    SupplierOptionDto,
    TransferHistory,
    transferHistoryListFromDto
} from '../types/transfer.types';
import {ToastType} from "@/shared/components/feedback/Toast/Toast.types";
import {useToast} from "@/shared/components/feedback/Toast/ToastContext";

export const useTransferData = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [excelDownloading, setExcelDownloading] = useState(false);
    const [histories, setHistories] = useState<TransferHistory[]>([]);
    const [totalTransferAmt, setTotalTransferAmt] = useState<number>(0);
    const [supplierOptions, setSupplierOptions] = useState<SupplierOptionDto[]>([]);

    /**
     * 송금 내역 목록 조회
     */
    const fetchHistories = useCallback(async (
        fromDate: string,
        toDate: string,
        suppId?: string
    ) => {
        try {
            setLoading(true);
            const response = await transferApi.getTransferList(fromDate, toDate, suppId);
            if (response) {
                const historyList = transferHistoryListFromDto(response);
                setHistories(historyList || []);
                setTotalTransferAmt(response.totalTransferAmt || 0);
            } else {
                setHistories([]);
                setTotalTransferAmt(0);
            }
        } catch (error: unknown) {
            console.error('송금 내역 조회 실패:', error);
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as { response?: { status?: number } };
                if (axiosError.response?.status !== 404) {
                    showToast('송금 내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                }
            } else {
                showToast('송금 내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            }
            setHistories([]);
            setTotalTransferAmt(0);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 협력사 목록 조회
     */
    const fetchSupplierOptions = useCallback(async () => {
        try {
            console.log('[TransferData] 협력사 목록 조회 시작');
            const response = await transferApi.getSupplierOptions();
            console.log('[TransferData] 협력사 목록 조회 응답:', response);
            setSupplierOptions(response || []);
        } catch (error: unknown) {
            console.error('[TransferData] 협력사 목록 조회 실패:', error);
            setSupplierOptions([]);
        }
    }, []);

    /**
     * 송금 신청
     */
    const requestTransfer = useCallback(async (
        suppId: string,
        reqVal: number,
        briefs?: string
    ) => {
        try {
            setLoading(true);
            const request: PostTransferRequestDto = {
                suppId,
                reqVal
            };
            if (briefs) {
                request.briefs = briefs;
            }
            const response = await transferApi.requestTransfer(request);

            if (response.success) {
                showToast('송금이 완료되었습니다.', { type: ToastType.SUCCESS });
                return response;
            } else {
                showToast(response.message || '송금에 실패했습니다.', { type: ToastType.ERROR });
                return null;
            }
        } catch (error: unknown) {
            console.error('송금 실패:', error);
            showToast('송금에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 송금 내역 엑셀 다운로드
     */
    const downloadExcel = useCallback(async (
        fromDate: string,
        toDate: string,
        suppId?: string
    ) => {
        try {
            setExcelDownloading(true);
            const blob = await transferApi.downloadExcel(fromDate, toDate, suppId);

            // 파일명 생성
            const formattedFromDate = fromDate.replace(/-/g, '');
            const formattedToDate = toDate.replace(/-/g, '');
            const filename = `송금내역_${formattedFromDate}_${formattedToDate}.xlsx`;

            // Blob 다운로드
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            showToast('엑셀 다운로드가 완료되었습니다.', { type: ToastType.SUCCESS });
        } catch (error: unknown) {
            console.error('엑셀 다운로드 실패:', error);
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setExcelDownloading(false);
        }
    }, [showToast]);

    return {
        loading,
        excelDownloading,
        histories,
        totalTransferAmt,
        supplierOptions,
        fetchHistories,
        fetchSupplierOptions,
        requestTransfer,
        downloadExcel,
        refreshHistories: fetchHistories
    };
};
